'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ShopLayout } from '@/components/shop/ShopLayout'
import { useCart } from '@/context/CartContext'
import { formatPrice } from '@/lib/format'

export default function CartPage() {
  const router = useRouter()
  const { items, subtotal, updateQuantity, removeItem } = useCart()

  if (items.length === 0) {
    return (
      <ShopLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Din kundvagn är tom</h1>
          <p className="mt-2 text-neutral-500">Utforska sortimentet och lägg till produkter.</p>
          <Link href="/" className="inline-block mt-6 px-6 py-3 rounded-xl bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium">
            Fortsätt handla
          </Link>
        </div>
      </ShopLayout>
    )
  }

  return (
    <ShopLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-8">Kundvagn</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.variantId}
                className="flex gap-4 p-4 rounded-2xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900"
              >
                <Link href={`/product/${item.slug}`} className="shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                    {item.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159M2.25 18h16.5" />
                        </svg>
                      </div>
                    )}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link href={`/product/${item.slug}`} className="font-medium text-neutral-900 dark:text-white hover:underline">
                        {item.title}
                      </Link>
                      {item.size && (
                        <p className="text-sm text-neutral-500 mt-0.5">Storlek: {item.size}</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.variantId)}
                      className="text-neutral-400 hover:text-red-500 p-1"
                      aria-label="Ta bort"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center rounded-lg border border-neutral-200 dark:border-neutral-700">
                      <button
                        onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-l-lg"
                      >
                        −
                      </button>
                      <span className="w-10 text-center text-sm font-medium text-neutral-900 dark:text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                        disabled={item.quantity >= item.maxStock}
                        className="w-8 h-8 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-r-lg disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                    <span className="font-semibold text-neutral-900 dark:text-white">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                  {item.quantity >= item.maxStock && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      Max antal i lager ({item.maxStock})
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 sticky top-24">
              <h2 className="font-semibold text-neutral-900 dark:text-white mb-4">Sammanfattning</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-neutral-600 dark:text-neutral-400">
                  <span>Delsumma</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-neutral-600 dark:text-neutral-400">
                  <span>Frakt</span>
                  <span>Beräknas i kassan</span>
                </div>
              </div>
              <div className="border-t border-neutral-100 dark:border-neutral-800 mt-4 pt-4 flex justify-between font-semibold text-neutral-900 dark:text-white">
                <span>Totalt</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <button
                onClick={() => router.push('/checkout')}
                className="w-full mt-6 px-6 py-3 rounded-xl bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors"
              >
                Till kassan
              </button>
              <Link href="/" className="block text-center mt-3 text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white">
                Fortsätt handla
              </Link>
            </div>
          </div>
        </div>
      </div>
    </ShopLayout>
  )
}
